import { useEffect, useRef, useState } from "react";
import { w3cwebsocket as W3CWebSocket } from "websocket";

import {
  HttpStatusError,
  fetchWebsocketTicket,
  getCredentials,
  getWebsocketReconnectDelay,
} from "../utils/Util";

// Send a message this often to keep the TCP connection busy so it doesn't get
// killed by the load balancer.
const IDLE_PING_INTERVAL_MS = 10 * 1000;

// How long a connection has to stay up before we consider it healthy and reset
// the backoff. The backend upgrades the socket before it does any work, so
// `onopen` fires even for connections that die immediately afterwards -
// resetting the attempt counter there pins the delay at its minimum and turns a
// struggling backend into a ~1 reconnect/sec loop per device, with every one of
// those reconnects triggering a full order fetch.
const HEALTHY_CONNECTION_MS = 30 * 1000;

/**
 * Pulls the restaurant ID out of a websocket URL, which always looks like
 * .../v1/ws/r/<restaurant_id>/... - that's where the ticket endpoint's
 * restaurant comes from, so the callers don't have to pass it separately.
 */
const restaurantIdFromUrl = (url: string): string | null => {
  const match = /\/v1\/ws\/r\/([^/?#]+)/.exec(url);
  return match ? match[1] : null;
};

const withTicket = (url: string, ticket: string): string =>
  `${url}${url.includes("?") ? "&" : "?"}ticket=${encodeURIComponent(ticket)}`;

interface UseReconnectingWebSocketOptions {
  /** Full websocket URL. The connection is torn down and reopened when it changes. */
  url: string;
  /** Set to false to keep the socket closed (e.g. while required params are missing). */
  enabled?: boolean;
  /** Called on every (re)connect. Local state built from deltas should be reset here. */
  onOpen?: () => void;
  /** Called with each parsed JSON message. */
  onMessage: (message: any) => void;
}

/**
 * Opens a websocket and keeps it open, reconnecting with exponential backoff.
 *
 * Returns whether the socket is currently disconnected, for showing a banner.
 */
export const useReconnectingWebSocket = ({
  url,
  enabled = true,
  onOpen,
  onMessage,
}: UseReconnectingWebSocketOptions): { disconnected: boolean } => {
  const [disconnected, setDisconnected] = useState(false);

  // Held in refs so that callers can pass inline callbacks without every render
  // tearing down the connection.
  const onOpenRef = useRef(onOpen);
  const onMessageRef = useRef(onMessage);
  useEffect(() => {
    onOpenRef.current = onOpen;
    onMessageRef.current = onMessage;
  });

  useEffect(() => {
    if (!enabled || !url) {
      return;
    }

    let closed = false;
    let socket: W3CWebSocket | null = null;
    let retryCount = 0;
    let reconnectTimeout: ReturnType<typeof setTimeout> | null = null;
    let healthyTimeout: ReturnType<typeof setTimeout> | null = null;
    let idlePing: ReturnType<typeof setInterval> | null = null;

    const clearConnectionTimers = () => {
      if (idlePing) {
        clearInterval(idlePing);
        idlePing = null;
      }
      if (healthyTimeout) {
        clearTimeout(healthyTimeout);
        healthyTimeout = null;
      }
    };

    const scheduleReconnect = () => {
      const delay = getWebsocketReconnectDelay(retryCount);
      retryCount++;
      reconnectTimeout = setTimeout(openWebSocket, delay);
    };

    // Resolves to the URL to connect with: the plain URL plus a freshly minted
    // single-use ticket. Falls back to the legacy cookie when the ticket
    // endpoint isn't there, so this frontend works against a backend deployed
    // before or after it.
    //
    // DEPRECATED: once every API instance serves /ws_ticket, drop the catch
    // below (and the getCredentials import) - the cookie is sent cross-site,
    // which is what let any third-party page open these sockets.
    const authenticatedUrl = async (): Promise<string> => {
      const restaurantId = restaurantIdFromUrl(url);
      if (!restaurantId) {
        return url;
      }

      try {
        return withTicket(url, await fetchWebsocketTicket(restaurantId));
      } catch (err) {
        if (err instanceof HttpStatusError && err.status === 404) {
          // Old backend: no ticket endpoint. Use cookie to pass auth info.
          // https://stackoverflow.com/questions/4361173/http-headers-in-websockets-client-api
          document.cookie = `x-tr-websocket-basic-auth=${getCredentials()}`;
          return url;
        }
        throw err;
      }
    };

    const openWebSocket = () => {
      if (closed) {
        return;
      }

      authenticatedUrl().then(startWebSocket, () => {
        // Minting the ticket failed (network blip, expired session). Back off
        // and try the whole handshake again rather than opening a socket that
        // is certain to be rejected.
        if (closed) {
          return;
        }
        setDisconnected(true);
        scheduleReconnect();
      });
    };

    const startWebSocket = (connectUrl: string) => {
      if (closed) {
        return;
      }

      const ws = new W3CWebSocket(connectUrl);
      socket = ws;

      ws.onopen = () => {
        setDisconnected(false);
        onOpenRef.current?.();

        healthyTimeout = setTimeout(() => {
          retryCount = 0;
        }, HEALTHY_CONNECTION_MS);

        idlePing = setInterval(() => {
          ws.send("1");
        }, IDLE_PING_INTERVAL_MS);
      };

      ws.onclose = () => {
        clearConnectionTimers();
        if (closed) {
          return;
        }
        setDisconnected(true);

        // Try opening the websocket again with exponential backoff and jitter.
        scheduleReconnect();
      };

      ws.onmessage = (evt) => {
        onMessageRef.current(JSON.parse(evt.data as string));
      };
    };

    openWebSocket();

    return () => {
      // Detach the handler before closing: otherwise onclose fires after the
      // component is gone and starts a reconnect loop that nothing owns or can
      // ever stop, and those orphans accumulate with every navigation.
      closed = true;
      clearConnectionTimers();
      if (reconnectTimeout) {
        clearTimeout(reconnectTimeout);
      }
      if (socket) {
        socket.onopen = () => {};
        socket.onclose = () => {};
        socket.onmessage = () => {};
        socket.close();
      }
    };
  }, [url, enabled]);

  return { disconnected };
};
